import React, {useEffect} from 'react';
import {useState} from "react";
import styles from './MatchResultModal.module.css';
import {formatFirstLetterOfName} from "../../../utils";
import CustomModal from "../../../UI/Modal/CustomModal";
import Spinner from "../../Spinner/Spinner";
import {useLocation, useNavigate} from "react-router-dom";
import {redirectToLoginIfInvalidToken} from "../../../api/api"; // Your custom styles

const initialScores = {
    player1Set1Games: '',
    player1Set2Games: '',
    player1Set3Games: '',
    player1Set1Tiebreak: '',
    player1Set2Tiebreak: '',
    player1Set3Tiebreak: '',
    player2Set1Games: '',
    player2Set2Games: '',
    player2Set3Games: '',
    player2Set1Tiebreak: '',
    player2Set2Tiebreak: '',
    player2Set3Tiebreak: '',
    set1Tiebreak: false,
    set2Tiebreak: false,
    set3Tiebreak: false
};

const mapMatchDataToState = (editingMatch) => {
    const mappedScores = {};

    // Iterate through each set and map the scores and tiebreak information
    editingMatch.setResults.forEach((setResult, index) => {
        const setNumber = index + 1;
        mappedScores[`player1Set${setNumber}Games`] = setResult.playerOneGames.toString();
        mappedScores[`player2Set${setNumber}Games`] = setResult.playerTwoGames.toString();
        mappedScores[`player1Set${setNumber}Tiebreak`] = setResult.playerOneTiebreakPoints ? setResult.playerOneTiebreakPoints.toString() : '';
        mappedScores[`player2Set${setNumber}Tiebreak`] = setResult.playerTwoTiebreakPoints ? setResult.playerTwoTiebreakPoints.toString() : '';
        mappedScores[`set${setNumber}Tiebreak`] = setResult.isTiebreak || false;
    });

    // Map other match properties like walkover and injury resignation
    return {
        ...initialScores, // Start with the initial structure to ensure all fields are present
        ...mappedScores, // Override with the scores from the match
    };
};
const MatchResultModal = ({
                              isOpen,
                              onRequestClose,
                              onSaveResult,
                              editingMatch,
                              matchId,
                              participant1Id,
                              participant2Id,
                              participant1FirstName,
                              participant1LastName,
                              participant2FirstName,
                              participant2LastName,
                              participant1PartnerFirstName,
                              participant1PartnerLastName,
                              participant2PartnerFirstName,
                              participant2PartnerLastName,
                              isThirdSetTiebreak,
                              matchFormat,
                              numberOfPointsInSet,
                              isUserSupervision,
                              showConfirmButton
                          }) => {
    // PRO_SET_TO_<N>_TB_AT_<N-1>: single set do N gemów, tiebreak rozgrywany przy (N-1):(N-1).
    // Sam tiebreak gra się standardowo do 7 pkt z 2-pkt przewagą — niezależnie od N.
    const PRO_SET_TARGETS = {
        PRO_SET_TO_9_TB_AT_8: 9,
        PRO_SET_TO_7_TB_AT_6: 7,
        PRO_SET_TO_6_TB_AT_5: 6,
    };
    const proSetTarget = PRO_SET_TARGETS[matchFormat];
    const isProSet = proSetTarget !== undefined;
    // Initialize a state with games for each set and optional tiebreaks
    const [scores, setScores] = useState(editingMatch ? mapMatchDataToState(editingMatch) : initialScores);
    const [walkover, setWalkover] = useState(editingMatch?.walkover ?? false);
    const [walkoverWinnerId, setWalkoverWinnerId] = useState(editingMatch?.walkoverWinnerId ?? null);
    const [injuryResignation, setInjuryResignation] = useState(editingMatch?.injuryResignation ?? false);
    const [injuryResignationParticipantId, setInjuryResignationParticipantId] = useState(editingMatch?.injuryResignationParticipantId ?? null);
    const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [clearMatchData, setClearMatchData] = useState(false);
    const [isSaving, setIsSaving] = useState(false);


    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        redirectToLoginIfInvalidToken(navigate, location.pathname);
    }, [navigate, location.pathname]);

    const handleWalkoverChange = (event) => {
        const isWalkover = event.target.checked;
        setWalkover(isWalkover);
        setWalkoverWinnerId(isWalkover ? walkoverWinnerId : null)
    };

    const handleInjuryResignationChange = (event) => {
        const isInjuryResignation = event.target.checked;
        setInjuryResignation(isInjuryResignation);
        setInjuryResignationParticipantId(isInjuryResignation ? injuryResignationParticipantId : null)
    };

    const handleWalkoverWinnerChange = (winnerId) => {
        setWalkoverWinnerId(winnerId);
    };

    const handleInjuryResignationParticipantChange = (participantId) => {
        setInjuryResignationParticipantId(participantId)
    };

    const handleScoreChange = (player, set, value) => {
        setScores(prev => ({...prev, [`${player}Set${set}Games`]: value}));
    };


    const handleTiebreakChange = (set, isChecked) => {
        setScores(prev => ({...prev, [`set${set}Tiebreak`]: isChecked}));
    };

    const handleTiebreakScoreChange = (player, set, value) => {
        setScores(prev => ({...prev, [`${player}Set${set}Tiebreak`]: value}));
    };

    const validateMatchData = () => {
        // Check for walkover and injury resignation conditions
        if (walkover) {
            if (!walkoverWinnerId) {
                return 'Wskaż zwyciężcę walkowera.';
            } else {
                // If walkover is selected, other validations are bypassed
                return '';
            }
        }

        if (injuryResignation) {
            if (!injuryResignationParticipantId) {
                return 'Wskaż kontuzjowanego.';
            } else {
                // If injury resignation is selected, other validations are bypassed
                return '';
            }
        }

        // Helper function to check if set scores are valid
        const isValidSet = (player1Games, player2Games) => {
            return (player1Games > 0 || player2Games > 0) && player1Games !== player2Games;
        };

        if (isProSet) {
            const N = proSetTarget;          // 9 / 7 / 6
            const tbAt = N - 1;              // 8 / 6 / 5 — pro-set "do N" odpala tiebreak przy (N-1):(N-1)
            const p1 = parseInt(scores.player1Set1Games || 0, 10);
            const p2 = parseInt(scores.player2Set1Games || 0, 10);
            if (!isValidSet(p1, p2)) {
                return 'Wpisz wynik pro-setu — liczba gemów nie może być równa.';
            }
            const max = Math.max(p1, p2);
            const min = Math.min(p1, p2);
            // Pro-set do N gemów: zwycięstwo N-x gdzie x ≤ N-2, lub N-(N-1) z tiebreakiem przy (N-1):(N-1)
            if (max !== N) {
                return `W pro-secie zwycięzca musi mieć ${N} gemów.`;
            }
            if (min === tbAt && !scores.set1Tiebreak) {
                return `Przy stanie ${N}:${tbAt} wymagany jest tiebreak (${tbAt}:${tbAt}).`;
            }
            if (min < tbAt - 1 && scores.set1Tiebreak) {
                return `Tiebreak rozgrywany jest tylko przy stanie ${tbAt}:${tbAt} (wynik ${N}:${tbAt}).`;
            }
            // Walidacja punktacji tiebreaku: do 7 punktów z 2-punktową przewagą (standard tenisowy, niezależny od N).
            if (min === tbAt && scores.set1Tiebreak) {
                const tb1 = parseInt(scores.player1Set1Tiebreak || 0, 10);
                const tb2 = parseInt(scores.player2Set1Tiebreak || 0, 10);
                if (tb1 === tb2) {
                    return 'Wpisz wynik tiebreaku — punkty nie mogą być równe.';
                }
                const tbMax = Math.max(tb1, tb2);
                const tbMin = Math.min(tb1, tb2);
                const proSetWinnerIsP1 = p1 > p2;
                const tbWinnerIsP1 = tb1 > tb2;
                if (proSetWinnerIsP1 !== tbWinnerIsP1) {
                    return 'Zwycięzca pro-setu musi też wygrać tiebreak.';
                }
                if (tbMax < 7) {
                    return 'Tiebreak rozgrywany jest do 7 punktów (z 2-punktową przewagą).';
                }
                if (tbMax - tbMin < 2) {
                    return 'Tiebreak wymaga 2-punktowej przewagi (np. 7:5, 8:6, 9:7, 10:8).';
                }
            }
            return '';
        }

        let player1SetWins = 0;
        let player2SetWins = 0;
        for (const setNumber of [1, 2, 3]) {
            const player1Games = parseInt(scores[`player1Set${setNumber}Games`] || 0, 10);
            const player2Games = parseInt(scores[`player2Set${setNumber}Games`] || 0, 10);
            const isTiebreakChecked = scores[`set${setNumber}Tiebreak`];

            if (player1Games === 0 && player2Games === 0 && !isTiebreakChecked) continue;

            if (player1Games === player2Games) {
                return `Nieprawidłowe dane w secie ${setNumber}. Liczba gemów w secie nie może być równa (np. ${player1Games}:${player2Games}).`;
            }

            // Super-tiebreak jako 3. set (turniej z isThirdSetTiebreak=true):
            // pierwszy do 10 z 2-punktową przewagą. Wartości wpisywane są w pola gemów
            // (checkbox „Tiebreak" w UI ukryty dla tego seta — linia 345).
            if (setNumber === 3 && isThirdSetTiebreak) {
                // Super-tiebreak rozgrywany jest WYŁĄCZNIE przy stanie 1:1 w setach.
                // Pętla iteruje sety w kolejności [1,2,3], więc tu setWins odzwierciedlają sety 1-2.
                if (player1SetWins === 2 || player2SetWins === 2) {
                    return 'Super-tiebreak w 3. secie rozgrywany jest tylko przy stanie 1:1 w setach — mecz został już rozstrzygnięty po 2 setach.';
                }
                const maxP = Math.max(player1Games, player2Games);
                const minP = Math.min(player1Games, player2Games);
                if (maxP < 10) {
                    return `Super-tiebreak w 3. secie rozgrywany jest do 10 punktów (z 2-punktową przewagą).`;
                }
                if (maxP - minP < 2) {
                    return `Super-tiebreak w 3. secie wymaga 2-punktowej przewagi (np. 10:8, 11:9, 12:10).`;
                }
                if (maxP > 10 && maxP - minP > 2) {
                    return `Wynik super-tiebreaka ${maxP}:${minP} jest niemożliwy — gra kończy się przy 2-punktowej przewadze.`;
                }
                if (player1Games > player2Games) player1SetWins++;
                else player2SetWins++;
                continue;
            }

            if (numberOfPointsInSet) {
                const N = numberOfPointsInSet;
                const maxG = Math.max(player1Games, player2Games);
                const minG = Math.min(player1Games, player2Games);

                if (N <= 4) {
                    if (maxG !== N) {
                        return `W secie ${setNumber} zwycięzca musi mieć dokładnie ${N} gemów (np. ${N}:0, ${N}:${N-1}).`;
                    }
                    if (minG === N - 1) {
                        if (!isTiebreakChecked) {
                            return `Przy wyniku ${N}:${N-1} w secie ${setNumber} wymagany jest tiebreak.`;
                        }
                    } else if (isTiebreakChecked) {
                        return `Tiebreak w secie ${setNumber} rozgrywany jest tylko przy wyniku ${N}:${N-1}.`;
                    }
                } else {
                    if (maxG < N) {
                        return `W secie ${setNumber} zwycięzca musi mieć co najmniej ${N} gemów.`;
                    }
                    if (maxG === N) {
                        if (maxG - minG < 2) {
                            return `W secie ${setNumber} przy wyniku ${maxG}:${minG} wymagana jest 2-gemowa różnica.`;
                        }
                        if (isTiebreakChecked) {
                            return `Tiebreak w secie ${setNumber} przy wyniku ${maxG}:${minG} nie jest rozgrywany (tiebreak gra się przy ${N+1}:${N}).`;
                        }
                    } else if (maxG === N + 1) {
                        if (minG === N - 1) {
                            if (isTiebreakChecked) {
                                return `Tiebreak w secie ${setNumber} przy wyniku ${maxG}:${minG} nie jest rozgrywany (tiebreak gra się przy ${N+1}:${N}).`;
                            }
                        } else if (minG === N) {
                            if (!isTiebreakChecked) {
                                return `Przy wyniku ${maxG}:${minG} w secie ${setNumber} wymagany jest tiebreak.`;
                            }
                        } else {
                            return `Niepoprawny wynik w secie ${setNumber} (${maxG}:${minG}).`;
                        }
                    } else {
                        return `Niepoprawny wynik w secie ${setNumber} (${maxG}:${minG}).`;
                    }
                }
            }

            if (isTiebreakChecked) {
                const tb1Raw = scores[`player1Set${setNumber}Tiebreak`];
                const tb2Raw = scores[`player2Set${setNumber}Tiebreak`];
                if (tb1Raw === '' || tb1Raw === undefined || tb2Raw === '' || tb2Raw === undefined) {
                    return `Wpisz wynik tiebreaku w secie ${setNumber}.`;
                }
                const tb1 = parseInt(tb1Raw, 10);
                const tb2 = parseInt(tb2Raw, 10);
                if (tb1 === tb2) {
                    return `Wpisz wynik tiebreaku w secie ${setNumber} — punkty nie mogą być równe.`;
                }
                const setWinnerIsP1 = player1Games > player2Games;
                const tbWinnerIsP1 = tb1 > tb2;
                if (setWinnerIsP1 !== tbWinnerIsP1) {
                    return `Zwycięzca seta ${setNumber} musi też wygrać tiebreak.`;
                }
                const tbMax = Math.max(tb1, tb2);
                const tbMin = Math.min(tb1, tb2);
                if (tbMax < 7) {
                    return `Tiebreak w secie ${setNumber} rozgrywany jest do 7 punktów (z 2-punktową przewagą).`;
                }
                if (tbMax - tbMin < 2) {
                    return `Tiebreak w secie ${setNumber} wymaga 2-punktowej przewagi (np. 7:5, 8:6, 9:7, 10:8).`;
                }
            }

            if (player1Games > player2Games) player1SetWins++;
            else player2SetWins++;
        }

        // Ensure one player won at least two sets
        if (player1SetWins < 2 && player2SetWins < 2) {
            return 'Gramy do dwóch wygranych setów.';
        }

        // Check for sequential set data validity
        for (let i = 2; i <= 3; i++) {
            const prevSet = i - 1;
            const player1PrevGames = parseInt(scores[`player1Set${prevSet}Games`] || 0, 10);
            const player2PrevGames = parseInt(scores[`player2Set${prevSet}Games`] || 0, 10);
            const player1Games = parseInt(scores[`player1Set${i}Games`] || 0, 10);
            const player2Games = parseInt(scores[`player2Set${i}Games`] || 0, 10);

            if ((player1Games > 0 || player2Games > 0) && (player1PrevGames === 0 && player2PrevGames === 0)) {
                return `Dla seta nr ${i} poprzedni set nie może zawierać samych zer.`;
            }
        }

        // If all validations pass
        return '';
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        setIsSaving(true);

        if (clearMatchData) {
            // Define logic to clear match data
            const clearedScores = {...initialScores}; // Assuming initialScores represent cleared state
            onSaveResult(matchId, clearedScores, false, null, false, null, true); // Add a parameter or modify how you signal that data should be cleared
        } else {
            // Existing validation and save logic
            const validationError = validateMatchData();
            if (validationError) {
                setIsCustomModalOpen(true);
                setModalMessage(validationError);
                setIsSaving(false);
                return;
            }

            // onSaveResult(matchId, scores, walkover, walkoverWinnerId, injuryResignation, injuryResignationParticipantId, false);
            await onSaveResult(matchId, scores, walkover, walkoverWinnerId, injuryResignation, injuryResignationParticipantId, false);
        }
        setIsSaving(false);
    };

    const createSetInputs = (setNumber) => {
        const isSet3SuperTiebreak = setNumber === 3 && isThirdSetTiebreak;
        const setLabel = isProSet
            ? `Pro-set (do ${proSetTarget}, TB ${proSetTarget - 1}:${proSetTarget - 1}):`
            : isSet3SuperTiebreak
                ? 'Super-tiebreak (do 10, przewaga 2 pkt):'
                : `Set ${setNumber}:`;
        return (
            <div key={setNumber} className={styles.setGroup}>
                <label>{setLabel}</label>
                <div className={styles.scoreInputs}>
                    <input
                        type="number"
                        min="0"
                        value={scores[`player1Set${setNumber}Games`]}
                        onChange={(e) => handleScoreChange('player1', setNumber, e.target.value)}
                        className={styles.scoreInput}
                    />
                    <input
                        type="number"
                        min="0"
                        value={scores[`player2Set${setNumber}Games`]}
                        onChange={(e) => handleScoreChange('player2', setNumber, e.target.value)}
                        className={styles.scoreInput}
                    />
                </div>
                {setNumber !== 3 || !isThirdSetTiebreak ? (
                    <div className={styles.tiebreakCheckbox}>
                        <input
                            type="checkbox"
                            checked={scores[`set${setNumber}Tiebreak`]}
                            onChange={(e) => handleTiebreakChange(setNumber, e.target.checked)}
                            id={`tiebreak${setNumber}`}
                        />
                        <label htmlFor={`tiebreak${setNumber}`}>Tiebreak</label>
                    </div>
                ) : null}
                {scores[`set${setNumber}Tiebreak`] && !isSet3SuperTiebreak && (
                    <div className={styles.tiebreakInputs}>
                        <input
                            type="number"
                            min="0"
                            value={scores[`player1Set${setNumber}Tiebreak`]}
                            onChange={(e) => handleTiebreakScoreChange('player1', setNumber, e.target.value)}
                            className={styles.scoreInput}
                        />
                        <input
                            type="number"
                            min="0"
                            value={scores[`player2Set${setNumber}Tiebreak`]}
                            onChange={(e) => handleTiebreakScoreChange('player2', setNumber, e.target.value)}
                            className={styles.scoreInput}
                        />
                    </div>
                )}
            </div>
        );
    };

    if (!isOpen) {
        return null;
    }

    const handleCloseCustomModal = () => {
        setIsCustomModalOpen(false);
    };

    const formatName = (firstName, lastName, partnerFirstName, partnerLastName) => {
        return (
            <>
                <span>{` ${lastName} ${formatFirstLetterOfName(firstName)}`}</span>
                {partnerFirstName && partnerLastName && (
                    <>
                        <span>{`/ ${partnerLastName} ${formatFirstLetterOfName(partnerFirstName)} `}</span>
                    </>
                )}
            </>
        );
    };


    return (
        <div className={styles.modalOverlay}>
            <CustomModal
                isOpen={isCustomModalOpen}
                onRequestClose={handleCloseCustomModal}
                message={modalMessage}
                isPositiveMessage={modalMessage.includes("Operacja")}
                cancelLabel="Zamknij"
                confirmButtonColor="red"
                cancelButtonColor="green"
            />
            <div className={styles.modal}>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.participantNames}>
                        <h5>{participant1LastName} {formatFirstLetterOfName(participant1FirstName)} <br/>
                            {participant1PartnerLastName} {formatFirstLetterOfName(participant1PartnerFirstName)}
                        </h5>
                        <h5>{participant2LastName} {formatFirstLetterOfName(participant2FirstName)}<br/>
                            {participant2PartnerLastName} {formatFirstLetterOfName(participant2PartnerFirstName)}
                        </h5>
                    </div>
                    {(isProSet ? [1] : [1, 2, 3]).map(createSetInputs)}
                    <div className={styles.checkboxGroup}>
                        <label>
                            <input
                                type="checkbox"
                                checked={walkover}
                                onChange={handleWalkoverChange}
                            />
                            Walkower
                        </label>
                        {walkover && (
                            <div className={styles.radioGroup}>
                                <p className={styles.question}>Kto wygrał ?</p>
                                <label>
                                    <input
                                        type="radio"
                                        name="walkoverWinner"
                                        onChange={() => handleWalkoverWinnerChange(participant1Id)}
                                        checked={walkoverWinnerId === participant1Id}
                                    />
                                    {formatName(participant1FirstName, participant1LastName, participant1PartnerFirstName, participant1PartnerLastName)}
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="walkoverWinner"
                                        onChange={() => handleWalkoverWinnerChange(participant2Id)}
                                        checked={walkoverWinnerId === participant2Id}
                                    />
                                    {formatName(participant2FirstName, participant2LastName, participant2PartnerFirstName, participant2PartnerLastName)}
                                </label>
                            </div>
                        )}
                        <label>
                            <input
                                type="checkbox"
                                checked={injuryResignation}
                                onChange={handleInjuryResignationChange}
                            />
                            Krecz
                        </label>
                        {injuryResignation && (
                            <div className={styles.radioGroup}>
                                <p className={styles.question}>Kto skreczował ?</p>
                                <label>
                                    <input
                                        type="radio"
                                        name="injuryResignationParticipant"
                                        onChange={() => handleInjuryResignationParticipantChange(participant1Id)}
                                        checked={injuryResignationParticipantId === participant1Id}
                                    />
                                    {formatName(participant1FirstName, participant1LastName, participant1PartnerFirstName, participant1PartnerLastName)}
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="injuryResignationParticipant"
                                        onChange={() => handleInjuryResignationParticipantChange(participant2Id)}
                                        checked={injuryResignationParticipantId === participant2Id}
                                    />
                                    {formatName(participant2FirstName, participant2LastName, participant2PartnerFirstName, participant2PartnerLastName)}
                                </label>
                            </div>
                        )}
                        {isUserSupervision &&
                            <label>
                                <input
                                    type="checkbox"
                                    checked={clearMatchData}
                                    onChange={(e) => setClearMatchData(e.target.checked)}
                                />
                                Wyczyść dane meczu
                            </label>
                        }
                    </div>
                    <div className={styles.actions}>
                        {isSaving ? (
                            <Spinner/>
                        ) : (
                            <>
                                <button type="submit" className={styles.saveButton}>Zapisz</button>
                                <button type="button" onClick={onRequestClose} className={styles.backButton}>Anuluj
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MatchResultModal;